import {
  type HereIsWhyItem,
  IconAccounts,
  IconBenefit,
  IconDifferenttype,
  IconMoneyplus,
  IconProtect,
  IconTransferin,
} from "@manifesthhqinc/ui-library";

export enum TransferOptionValues {
  employerSponsored = "EMPLOYER_SPONSORED",
  ira = "IRA",
  managedIRA = "MANAGED_IRA",
  wait = "WAIT",

  // This is the default value the front end uses if no response is returned from the backend
  // We'll also use this to determine if the user has options available. If all answers are "NO_OPTION",
  // then we can assume no options are available for the user
  noOption = "NO_OPTION_YET",
}
export type TransferOptionsResponse = {
  FIRST: TransferOptionValues;
  SECOND: TransferOptionValues;
} | null;

export type OptionContent = {
  title: string;
  description: string;
  hereIsWhyContent: HereIsWhyItem[];
  value: TransferOptionValues;
};

export type TransferOptionContent = {
  FIRST: OptionContent | null;
  SECOND: OptionContent | null;
};

const getTransferOptionContent = (option: TransferOptionValues): OptionContent | null => {
  switch (option) {
    case TransferOptionValues.employerSponsored:
      return {
        value: TransferOptionValues.employerSponsored,
        title: "Employer-sponsored plan",
        description:
          "Consolidating your accounts into your current employer-sponsored account is the best way to manage your savings in a single place.",
        hereIsWhyContent: [
          {
            icon: <IconTransferin size="large" color="dark" />,
            text: "Since you have a current employer retirement plan, you might be able to take advantage of employer matching — that means more savings for you",
          },
          {
            icon: <IconDifferenttype size="large" color="dark" />,
            text: "Employer-sponsored accounts have fiduciary and bankruptcy protections not shared by IRAs",
          },
          {
            icon: <IconMoneyplus size="large" color="dark" />,
            text: "Your plan can offer you bonus features like the ability to take loans in case of a financial emergency",
          },
          {
            icon: <IconMoneyplus size="large" color="dark" />,
            text: "Automate your retirement investments every time you get paid ",
          },
        ],
      };
    case TransferOptionValues.ira:
      return {
        value: TransferOptionValues.ira,
        title: "Self-directed IRA",
        description:
          "An individual retirement account (IRA) stays consistent throughout your career and can be set up to maximize your retirement outcomes for you.  ",
        hereIsWhyContent: [
          {
            icon: <IconDifferenttype color="dark" size="large" />,
            text: `IRAs offer you the flexibility to manage investments your way — more freedom and control`,
          },
          {
            icon: <IconAccounts color="dark" size="large" />,
            text: "Keep your IRA with you through retirement, without worrying every time you switch jobs",
          },
          {
            icon: <IconBenefit color="dark" size="large" />,
            text: "Depending on how you invest your savings, this type of account can maximize returns, while minimizing fees",
          },
        ],
      };
    case TransferOptionValues.managedIRA:
      return {
        value: TransferOptionValues.managedIRA,
        title: "Robo-advised IRA",
        description:
          "An individual retirement account (IRA) stays consistent throughout your career and can be set up to maximize your retirement outcomes for you.",
        hereIsWhyContent: [
          {
            icon: <IconDifferenttype color="dark" size="large" />,
            text: `IRAs are an easy way to set personalized investment goals without worrying about the hassles of consistently managing the account yourself`,
          },
          {
            icon: <IconAccounts color="dark" size="large" />,
            text: "As the market moves and you move toward retirement, a good IRA advisor can keep your savings optimized",
          },
          {
            icon: <IconBenefit color="dark" size="large" />,
            text: "These accounts are known for low fees and can have a high potential to maximize returns",
          },
        ],
      };
    case TransferOptionValues.wait:
      return {
        value: TransferOptionValues.wait,
        title: "Wait to transfer into an employer plan",
        description:
          "Sometimes it’s wise to be patient. If you move your savings into an IRA now, it can be difficult to move your funds bacak once you have a new employer-sponsored account. Also, you may have multiple accounts to juggle once you start your next position. ",
        hereIsWhyContent: [
          {
            icon: <IconMoneyplus size="large" color="dark" />,
            text: "Consolidating your accounts into an employer-sponsored account is often the best way to manage your savings in a single location",
          },
          {
            icon: <IconProtect size="large" color="dark" />,
            text: "Employer-sponsored accounts have fiduciary and bankruptcy protections not shared by IRAs",
          },
          {
            icon: <IconMoneyplus size="large" color="dark" />,
            text: "Your plan can offer you bonus features like the ability to take loans in case of a financial emergency",
          },
        ],
      };
    default:
      return null;
  }
};

export default getTransferOptionContent;
